import { ThemeEvents } from '@theme/events';

class StickyMobilePurchase extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.productForm = this.section?.querySelector('product-form-component');
    this.stickyButton = this.querySelector('[data-sticky-add-to-cart]');
    this.titleElement = this.querySelector('[data-sticky-title]');
    this.mainTarget =
      this.productForm?.querySelector('[ref="productFormButtons"]') ||
      this.productForm?.querySelector('[ref="addToCartButtonContainer"]') ||
      this.productForm;

    if (!this.section || !this.productForm || !(this.stickyButton instanceof HTMLButtonElement)) {
      this.remove();
      return;
    }

    this.syncFromMainButton();

    this.handleStickyClick = this.handleStickyClick.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);

    this.stickyButton.addEventListener('click', this.handleStickyClick);
    this.section.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    this.buttonObserver = new MutationObserver(() => this.syncFromMainButton());
    this.observeMainButton();

    if (this.mainTarget) {
      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.dataset.mainVisible = entry.isIntersecting ? 'true' : 'false';
        },
        {
          threshold: [0, 0.2, 0.45],
        }
      );

      this.visibilityObserver.observe(this.mainTarget);
    }
  }

  disconnectedCallback() {
    this.stickyButton?.removeEventListener('click', this.handleStickyClick);
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    this.buttonObserver?.disconnect();
    this.visibilityObserver?.disconnect();
  }

  getMainButton() {
    return this.productForm?.querySelector('[ref="addToCartButton"]');
  }

  observeMainButton() {
    const mainButton = this.getMainButton();
    if (!mainButton || !this.buttonObserver) return;

    this.buttonObserver.disconnect();
    this.buttonObserver.observe(mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  syncFromMainButton() {
    const mainButton = this.getMainButton();
    if (!(mainButton instanceof HTMLButtonElement) || !(this.stickyButton instanceof HTMLButtonElement)) return;

    const visibleText =
      mainButton.querySelector('.add-to-cart-text__content')?.textContent?.trim() || mainButton.textContent?.trim() || '';

    this.stickyButton.textContent = visibleText;
    this.stickyButton.disabled = mainButton.disabled;
    this.toggleAttribute('hidden', false);
  }

  handleStickyClick(event) {
    event.preventDefault();

    const mainButton = this.getMainButton();
    if (!(mainButton instanceof HTMLButtonElement)) return;

    if (mainButton.disabled) {
      mainButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    mainButton.click();
  }

  handleVariantUpdate(event) {
    if (!this.productForm || event.detail.data.productId !== this.productForm.dataset.productId) {
      return;
    }

    requestAnimationFrame(() => {
      const nextTitle = event.detail.data.html.querySelector('.product-details h1');
      if (nextTitle instanceof HTMLElement && this.titleElement) {
        this.titleElement.textContent = nextTitle.textContent?.trim() || this.titleElement.textContent;
      }

      this.observeMainButton();
      this.syncFromMainButton();
    });
  }
}

if (!customElements.get('sticky-mobile-purchase')) {
  customElements.define('sticky-mobile-purchase', StickyMobilePurchase);
}
